<header class="header">

  <!-- ── Row 1: Utility bar — logo · search · account · cart · wishlist ─── -->
  <div class="header__utility">
    <div class="header__utility-inner">

      <button class="header__hamburger" type="button" [attr.aria-expanded]="isMenuOpen()" aria-controls="mobile-menu" [attr.aria-label]="isMenuOpen() ? 'Cerrar menú' : 'Abrir menú'" (click)="toggleMenu()">
        @if (isMenuOpen()) {
        <app-icon [icon]="closeIcon" [size]="22" color="currentColor" />
        } @else {
        <app-icon [icon]="menuIcon" [size]="22" color="currentColor" />
        }
      </button>

      <a class="header__logo" routerLink="/" (click)="onLogoClick($event)" aria-label="Perrón Store – Inicio">
        <img class="header__logo-img" ngSrc="/images/logo/logo.png" alt="Perrón Store" width="520" height="373" priority />
      </a>

      <div class="header__search">
        <app-search-bar
          placeholder="Buscar cartas, juegos..."
          (queryChange)="onSearchInput($event)"
          (search)="onSearchDebounced($event)"
          (submit)="onSearchCommit($event)"
          (focusIn)="onSearchFocus()"
          (blurOut)="onSearchBlur()"
        />
      </div>

      <div class="header__actions">
        <div class="header__user-wrapper">
          <button class="header__action-btn" type="button" [attr.aria-expanded]="isUserMenuOpen()" aria-haspopup="true" aria-label="Mi cuenta" (click)="toggleUserMenu()">
            @if (userAvatar(); as avatar) {
              <img class="header__user-avatar" [src]="avatar" [alt]="currentUser()?.name ?? 'Mi cuenta'" (error)="onUserAvatarError()" />
            } @else {
              <app-icon [icon]="userIcon" [size]="20" color="currentColor" />
            }
          </button>

          @if (isUserMenuOpen()) {
          <div class="header__user-menu" role="menu">
            @if (!isLoggedIn()) {
            <div class="px-2 py-1">
              <button class="border border-gray-200 w-full px-2 py-1 rounded-md bg-gray-50 cursor-pointer hover:bg-amber-600 hover:text-white transition-colors" type="button" role="menuitem" (click)="onLoginClick()">
                Iniciar sesión
              </button>
            </div>
            <button class="text-center w-full px-2 py-1 text-xs text-gray-600 hover:text-amber-600 transition-colors cursor-pointer" type="button" role="menuitem" (click)="onRegisterClick()">
              Crear cuenta
            </button>
            } @else {
            <div class="header__user-menu-header">Hola, {{ currentUser()?.name ?? 'Jugador' }}</div>
            <div class="header__pp">
              @if (pointsLoading()) {
                <span class="header__pp-skeleton" aria-hidden="true"></span>
              } @else if (!pointsLoadError()) {
                <p class="header__pp-balance">
                  <app-pp-coin [size]="16" />{{ withPoints(ppCopy.balance, pointsBalance()) }}
                </p>
                @if (pointsThreshold() !== null) {
                  @if (pointsEarlyAccess()) {
                    <p class="header__pp-tier header__pp-tier--active">{{ ppCopy.tierActive }}</p>
                  } @else if (pointsMissing(); as missing) {
                    <p class="header__pp-tier">
                      <app-pp-coin [size]="14" />{{ withMissing(ppCopy.tierPending, missing) }}
                    </p>
                  }
                }
              }
              <button class="header__pp-cta" type="button" role="menuitem" (click)="onOpenPerronPointsModal()">
                {{ ppCopy.cta }}
              </button>
            </div>
            <div class="header__user-menu-divider"></div>
            <a class="header__user-menu-item" routerLink="/perfil" role="menuitem" (click)="closeUserMenu()">{{ profileCopy.USER_MENU.PROFILE }}</a>
            <a class="header__user-menu-item" routerLink="/perfil/pedidos" role="menuitem" (click)="closeUserMenu()">{{ profileCopy.USER_MENU.ORDERS }}</a>
            <a class="header__user-menu-item" routerLink="/perfil/deseos" role="menuitem" (click)="closeUserMenu()">{{ profileCopy.USER_MENU.WISHLIST }}</a>
            <div class="header__user-menu-divider"></div>
            <button class="header__user-menu-item header__user-menu-item--danger" type="button" role="menuitem" (click)="onLogout()">
              Salir
            </button>
            }
          </div>
          }
        </div>

        <div class="header__cart-wrapper">
          <button class="header__action-btn" type="button" aria-label="Carrito de compras" (click)="onCartClick()">
            <app-icon [icon]="cartIcon" [size]="20" color="currentColor" />
          </button>
          @if (cartCount() > 0) {
          <span class="header__cart-badge" [attr.aria-label]="cartCount() + ' productos en el carrito'">
            {{ cartCount() }}
          </span>
          }
        </div>
        <!-- Con sesión, enlace a la lista de deseos; sin sesión, el login (sin toast). -->
        @if (isLoggedIn()) {
        <a class="header__action-btn" routerLink="/perfil/deseos" [attr.aria-label]="profileCopy.HEADER_WISHLIST_ARIA" (click)="onWishlistClick()">
          <app-icon [icon]="wishlistIcon" [size]="20" color="currentColor" />
        </a>
        } @else {
        <button class="header__action-btn" type="button" [attr.aria-label]="profileCopy.HEADER_WISHLIST_ARIA" (click)="onWishlistClick()">
          <app-icon [icon]="wishlistIcon" [size]="20" color="currentColor" />
        </button>
        }
      </div>

    </div>
  </div>

  <!-- ── Mobile search bar (below navbar, sticky) ─────────────────────────── -->
  <div class="header__mobile-search">
    <app-search-bar
      placeholder="Buscar cartas, juegos..."
      (queryChange)="onSearchInput($event)"
      (search)="onSearchDebounced($event)"
      (submit)="onSearchCommit($event)"
      (focusIn)="onSearchFocus()"
      (blurOut)="onSearchBlur()"
    />
  </div>

  <!-- ── Row 2: Category nav — Tienda · Juegos (mega) · Blog · … ──────────── -->
  <nav class="header__category-nav" aria-label="Navegación principal">
    <div class="header__category-nav-inner">
      <ul class="header__nav-list" role="list">

        @for (item of mainNavItems(); track item.label) {
        <!-- "Juegos" sólo existe si el índice de secciones trajo algo: sin datos
             no dejamos un trigger que abre un panel vacío. -->
        @if (item.megaKey !== 'juegos' || showGamesNav()) {
        <li class="header__nav-item" [class.header__nav-item--has-mega]="item.hasMega" (mouseleave)="item.hasMega ? closeMega() : null">
          @if (item.hasMega && item.megaKey) {

          <!-- Trigger megamenú -->
          <button class="header__nav-link header__nav-trigger" type="button"
            [class.header__nav-link--active]="activeMega() === item.megaKey"
            [attr.aria-expanded]="activeMega() === item.megaKey"
            aria-haspopup="true"
            (mouseenter)="openMega(item.megaKey)"
            (click)="toggleMega(item.megaKey)">
            {{ item.label }}
            <app-icon [icon]="chevronIcon" [size]="13" color="currentColor" />
          </button>

          <!-- ═══ TIENDA mega menu ═══ -->
          @if (item.megaKey === 'tienda' && activeMega() === 'tienda') {
          <div class="header__mega header__mega--tienda" role="dialog" aria-label="Tienda — Categorías" (mouseenter)="openMega('tienda')">

            <!-- Col izquierda: categorías -->
            <ul class="header__mega-categories" role="list">
              @for (cat of tiendaCategories(); track cat.id) {
              <li>
                <a class="header__mega-cat-link"
                  [class.header__mega-cat-link--active]="hoveredCategory() === cat.id"
                  [class.header__mega-cat-link--synthetic]="cat.synthetic"
                  [routerLink]="cat.route"
                  (mouseenter)="hoveredCategory.set(cat.id)"
                  (click)="closeMega()">
                  @if (cat.synthetic) {
                    <span class="header__nav-dot" aria-hidden="true"></span>
                  }
                  {{ cat.name }}
                </a>
              </li>
              }
            </ul>

            <!-- Col centro: subcategorías (hover) o Más populares (empty state) -->
            <div class="header__mega-info">
              @if (activeCategoryDetail(); as detail) {
              <h3 class="header__mega-detail-title">{{ detail.name }}</h3>

              <div class="header__mega-subcats">
                @if (detail.subcategories.length) {
                <p class="header__mega-subcats-label">Subcategorías</p>
                <ul class="header__mega-subcats-list" role="list">
                  @for (sub of detail.subcategories; track sub.link) {
                  <li>
                    <a class="header__mega-subcat-link" [routerLink]="sub.link" (click)="closeMega()">
                      <app-icon [icon]="chevronRightIcon" [size]="14" color="currentColor" />
                      {{ sub.name }}
                    </a>
                  </li>
                  }
                </ul>
                }
                <a class="header__mega-cta" [routerLink]="detail.route" (click)="closeMega()">
                  Ver todo en {{ detail.name }} &rarr;
                </a>
              </div>
              } @else if (popularStatus() === 'loading') {
              <p class="header__mega-hint">Cargando productos...</p>
              } @else if (popularProducts().length) {
              <p class="header__mega-info-title">Más populares</p>
              <div class="header__mega-recent-grid">
                @for (prod of popularProducts(); track prod.id) {
                <a class="header__mega-recent-card" [routerLink]="prod.route" (click)="closeMega()">
                  @if (prod.isNew || (prod.isOnSale && prod.discountPercent) || prod.isPreSale) {
                  <div class="header__mega-recent-badges">
                    @if (prod.isNew) { <app-badge variant="new" [overlay]="true">Novedad</app-badge> }
                    @if (prod.isOnSale && prod.discountPercent) { <app-badge variant="sale" [overlay]="true">-{{ prod.discountPercent }}%</app-badge> }
                    @if (prod.isPreSale) { <app-badge variant="pre-sale" [overlay]="true">Pre-venta</app-badge> }
                  </div>
                  }
                  <img class="header__mega-recent-img" [src]="prod.imageUrl" [alt]="prod.name" loading="lazy" (error)="fallbackImage($event)" />
                  <span class="header__mega-recent-name">{{ prod.name }}</span>
                  <span class="header__mega-recent-price">{{ prod.priceFormatted }}</span>
                </a>
                }
              </div>
              } @else {
              <p class="header__mega-hint">Pasa el cursor sobre una categoría para ver detalles.</p>
              }
            </div>

            <!-- Col derecha: productos recientes (categoría hovered) o globales (empty state) -->
            <div class="header__mega-recent">
              <p class="header__mega-recent-title">Agregados recientemente</p>
              @if (hoveredCategory() !== null && recentProductsForHovered().length) {
              <div class="header__mega-recent-grid">
                @for (prod of recentProductsForHovered(); track prod.id) {
                <a class="header__mega-recent-card" [routerLink]="prod.route" (click)="closeMega()">
                  @if (prod.isNew || (prod.isOnSale && prod.discountPercent) || prod.isPreSale) {
                  <div class="header__mega-recent-badges">
                    @if (prod.isNew) { <app-badge variant="new" [overlay]="true">Novedad</app-badge> }
                    @if (prod.isOnSale && prod.discountPercent) { <app-badge variant="sale" [overlay]="true">-{{ prod.discountPercent }}%</app-badge> }
                    @if (prod.isPreSale) { <app-badge variant="pre-sale" [overlay]="true">Pre-venta</app-badge> }
                  </div>
                  }
                  <img class="header__mega-recent-img" [src]="prod.imageUrl" [alt]="prod.name" loading="lazy" (error)="fallbackImage($event)" />
                  <span class="header__mega-recent-name">{{ prod.name }}</span>
                  <span class="header__mega-recent-price">{{ prod.priceFormatted }}</span>
                </a>
                }
              </div>
              } @else if (!showGlobalGrids()) {
              <p class="header__mega-hint">Aún no hay novedades en esta categoría.</p>
              } @else if (globalRecentStatus() === 'loading') {
              <p class="header__mega-hint">Cargando novedades...</p>
              } @else if (globalRecentProducts().length) {
              <div class="header__mega-recent-grid">
                @for (prod of globalRecentProducts(); track prod.id) {
                <a class="header__mega-recent-card" [routerLink]="prod.route" (click)="closeMega()">
                  @if (prod.isNew || (prod.isOnSale && prod.discountPercent) || prod.isPreSale) {
                  <div class="header__mega-recent-badges">
                    @if (prod.isNew) { <app-badge variant="new" [overlay]="true">Novedad</app-badge> }
                    @if (prod.isOnSale && prod.discountPercent) { <app-badge variant="sale" [overlay]="true">-{{ prod.discountPercent }}%</app-badge> }
                    @if (prod.isPreSale) { <app-badge variant="pre-sale" [overlay]="true">Pre-venta</app-badge> }
                  </div>
                  }
                  <img class="header__mega-recent-img" [src]="prod.imageUrl" [alt]="prod.name" loading="lazy" (error)="fallbackImage($event)" />
                  <span class="header__mega-recent-name">{{ prod.name }}</span>
                  <span class="header__mega-recent-price">{{ prod.priceFormatted }}</span>
                </a>
                }
              </div>
              } @else {
              <p class="header__mega-hint">No hay novedades por ahora.</p>
              }
            </div>

            <!-- Franja inferior: quick links -->
            <div class="header__mega-quicklinks">
              @for (link of tiendaQuickLinks; track link.label) {
              <a class="header__mega-quicklink" [routerLink]="link.route" [queryParams]="link.queryParams ?? {}" (click)="closeMega()">
                {{ link.label }}
              </a>
              }
            </div>

          </div>
          }

          <!-- ═══ JUEGOS mega menu — directorio plano de secciones ═══
               A diferencia del de Tienda, este panel se renderiza SIEMPRE y se
               oculta por CSS: sus enlaces /juegos/:slug son SEO-críticos y con
               un `@if` sobre `activeMega()` nunca saldrían en el HTML del SSR.
               Son 8 anchors; el mega de Tienda pesa demasiado para hacer lo mismo. -->
          @if (item.megaKey === 'juegos') {
          <div class="header__mega header__mega--juegos"
            [class.header__mega--hidden]="activeMega() !== 'juegos'"
            role="dialog" aria-label="Juegos — Secciones" (mouseenter)="openMega('juegos')">
            <ul class="header__mega-games" role="list">
              @for (game of games(); track game.slug) {
              <li>
                <a class="header__mega-game"
                  [style.--game-accent]="game.accentColor || null"
                  [routerLink]="['/juegos', game.slug]"
                  (click)="closeMega()">
                  @if (game.logoUrl) {
                  <img class="header__mega-game-logo" [src]="game.logoUrl" [alt]="game.name" loading="lazy" />
                  }
                  <!-- `shortName` (cae a `name` si el admin no lo puso): en una
                       tarjeta de 190px "Dragon Ball Super Card Game Fusion World"
                       parte en tres líneas. El nombre completo sigue disponible
                       en el `alt` del logo. -->
                  <span class="header__mega-game-name">{{ game.shortName }}</span>
                </a>
              </li>
              }
            </ul>
          </div>
          }

          } @else {

          <a class="header__nav-link"
            [class.header__nav-link--accent]="item.accent"
            [routerLink]="item.route"
            routerLinkActive="header__nav-link--active"
            (click)="closeMenu()">
            @if (item.accent) {
              <span class="header__nav-dot" aria-hidden="true"></span>
            }
            {{ item.label }}
          </a>

          }
        </li>
        }
        }

      </ul>
    </div>
  </nav>

</header>

<!-- ── Mobile drawer ─────────────────────────────────────────────────────── -->
<div class="header__drawer" [class.header__drawer--open]="isMenuOpen()" id="mobile-menu" [attr.aria-hidden]="!isMenuOpen()">
  <!-- Links principales -->
  <nav aria-label="Navegación principal (móvil)">
    <ul class="header__drawer-nav" role="list">
      @for (item of mainNavItems(); track item.label) {
      @if (!item.hasMega || item.route) {
      <li>
        <a class="header__drawer-link" [routerLink]="item.route" routerLinkActive="header__drawer-link--active" (click)="closeMenu()">
          {{ item.label }}
        </a>
      </li>
      }
      }
    </ul>
  </nav>

  <!-- Juegos (accordion) — sólo si el índice de secciones trajo algo -->
  @if (showGamesNav()) {
  <div class="header__drawer-section">
    <button class="header__drawer-section-toggle" type="button"
      [attr.aria-expanded]="expandedDrawerSection() === 'juegos'"
      (click)="toggleDrawerSection('juegos')">
      <span class="header__drawer-section-title">Juegos</span>
      <app-icon [icon]="chevronIcon" [size]="16" color="currentColor"
        [class.header__drawer-chevron--open]="expandedDrawerSection() === 'juegos'" />
    </button>
    @if (expandedDrawerSection() === 'juegos') {
      <ul class="header__drawer-nav header__drawer-nav--nested" role="list">
        @for (game of games(); track game.slug) {
          <li>
            <a class="header__drawer-link header__drawer-link--nested" [routerLink]="['/juegos', game.slug]" (click)="closeMenu()">
              {{ game.name }}
            </a>
          </li>
        }
      </ul>
    }
  </div>
  }

  <!-- Categorías (accordion) -->
  <div class="header__drawer-section">
    <button class="header__drawer-section-toggle" type="button"
      [attr.aria-expanded]="expandedDrawerSection() === 'categorias'"
      (click)="toggleDrawerSection('categorias')">
      <span class="header__drawer-section-title">Categorías</span>
      <app-icon [icon]="chevronIcon" [size]="16" color="currentColor"
        [class.header__drawer-chevron--open]="expandedDrawerSection() === 'categorias'" />
    </button>
    @if (expandedDrawerSection() === 'categorias') {
      <ul class="header__drawer-nav header__drawer-nav--nested" role="list">
        @for (cat of tiendaCategories(); track cat.id) {
          <li>
            <a class="header__drawer-link header__drawer-link--nested" [routerLink]="cat.route" (click)="closeMenu()">
              {{ cat.name }}
            </a>
          </li>
        }
      </ul>
    }
  </div>



</div>

<!-- ── Backdrop ──────────────────────────────────────────────────────────── -->
<div class="header__backdrop" [class.header__backdrop--visible]="isMenuOpen()" aria-hidden="true" (click)="closeMenu()"></div>
